import { isSafeUrl } from './htmlPolicy';

// A merchant will produce `props: {}`. So will a node the builder has just dropped and nobody has
// filled in yet. Every element reads what it needs through these, so a missing prop is a default and
// never a crash, and a prop of the wrong type never crosses into a component as `any`.

/** An element's props as they were stored: unknown until one of the readers below names a shape. */
export type ElementProps = Readonly<Record<string, unknown>>;

// Reads one prop as trimmed text, or the fallback when the merchant has not written it yet.
export function textProp(props: ElementProps, key: string, fallback = ''): string {
  const value = props[key];
  if (typeof value === 'string') return value.trim() === '' ? fallback : value;
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : fallback;
}

// Reads one prop as a finite number, or the fallback when it is anything else.
export function numberProp(props: ElementProps, key: string, fallback: number): number {
  const value = props[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

// Reads one prop as a flag, treating a missing prop as the fallback rather than as false.
export function boolProp(props: ElementProps, key: string, fallback = false): boolean {
  const value = props[key];
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === 'false') return value === 'true';
  return fallback;
}

// Reads one row of a list as the line it draws: the `{ text }` the inspector stores, or a bare string.
const lineOf = (item: unknown): string => {
  if (typeof item === 'string') return item.trim();
  if (typeof item !== 'object' || item === null) return '';
  const text = (item as Record<string, unknown>).text;
  return typeof text === 'string' ? text.trim() : '';
};

/**
 * Reads one prop as a list of lines, dropping whatever in it is not text a page can draw.
 *
 * A `list` control stores a row per line — `[{ text: 'Free delivery' }]` — because its item fields are
 * named, and that is what the catalog's defaults and every preset write. Keeping only bare strings is
 * what once made a merchant's whole list of benefits render as an empty `<ul>`.
 */
export function listProp(props: ElementProps, key: string, max = 200): string[] {
  const value = props[key];
  if (typeof value === 'string') return value.split('\n').map((line) => line.trim()).filter(Boolean).slice(0, max);
  if (!Array.isArray(value)) return [];
  return value.map(lineOf).filter((line) => line !== '').slice(0, max);
}

// Reads one prop as one of a named set, or the fallback when it names none of them.
export function choiceProp<T extends string>(
  props: ElementProps,
  key: string,
  allowed: readonly T[],
  fallback: T
): T {
  const value = props[key];
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

/** How a link opens. A new tab always drops its opener, which is the only unsafe part of `_blank`. */
export const LINK_TARGETS = ['_self', '_blank'] as const;

// Resolves how a link opens, and the `rel` that has to come with it.
export function linkTarget(props: ElementProps): { target?: string; rel?: string } {
  const target = choiceProp(props, 'target', LINK_TARGETS, '_self');
  return target === '_blank' ? { target, rel: 'noopener noreferrer' } : {};
}

/** Which way words run. `auto` lets the browser read it off the first strong character in the copy. */
export const TEXT_DIRECTIONS = ['auto', 'rtl', 'ltr'] as const;

// Reads which way an element's words run, so an Arabic line inside a French page is not laid out backwards.
export function textDirection(props: ElementProps): (typeof TEXT_DIRECTIONS)[number] {
  return choiceProp(props, 'direction', TEXT_DIRECTIONS, 'auto');
}

// Reads one prop as an address the page may point at, or nothing when its scheme could script.
export function urlProp(props: ElementProps, key: string, allowDataImage = false): string | undefined {
  const value = props[key];
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const url = value.trim();
  return isSafeUrl(url, allowDataImage) ? url : undefined;
}

// Splits written copy into the paragraphs a blank line between two lines asks for.
export function paragraphsOf(body: string): string[] {
  return body.split(/\n{2,}/).map((part) => part.trim()).filter((part) => part !== '');
}
